import { Icon } from '../components/icons/Icon'

export interface ShellContext {
  tenantName?: string | null
  region?: string | null
}

interface ShellContextIndicatorProps {
  context: ShellContext | null | undefined
}

/**
 * IB-27 S5 (FR-M23.1 chrome half, `docs/design/ib-27-s5-shell-chrome-design.md` §1) — the topbar
 * tenant/region indicator. Reads the caller's OWN bound session context (the `context` block
 * additively returned by `GET /me/ui-config`) — never a client-guessed or previous-session-cached
 * value; a fresh shell mount always re-fetches (see `NavShell`), so there is no stale flash across
 * a logout/login.
 *
 * Renders ONLY the fields actually present. `tenantName`/`region` are each independently optional
 * (the backend may send `null` for a tenant/JWT that carries no residency region) — an absent
 * field is cleanly omitted, never a placeholder/dead chip (CLAUDE.md §4 "no dead buttons"). If
 * BOTH are absent (e.g. `context` missing entirely, or the fetch hasn't resolved/failed), the
 * whole indicator renders nothing rather than an empty shell.
 *
 * Territory and "as-of" are deliberately NOT part of this indicator this pass — territory is a
 * multi-valued visibility axis with no single "current" value, and no shell-level as-of concept
 * exists yet (design VERDICT, DoR Q1). Both are named follow-on stories, not rendered here even as
 * an inert placeholder.
 */
export function ShellContextIndicator({ context }: ShellContextIndicatorProps) {
  const tenantName = context?.tenantName
  const region = context?.region
  if (!tenantName && !region) return null

  return (
    <div className="iw-shell-context" aria-label="Current tenant and region">
      {tenantName ? (
        <span className="iw-shell-context__pill" title={`Tenant: ${tenantName}`}>
          <Icon name="building2" size={13} />
          <span>{tenantName}</span>
        </span>
      ) : null}
      {region ? (
        <span className="iw-shell-context__pill" title={`Region: ${region}`}>
          <Icon name="map-pin" size={13} />
          <span>{region}</span>
        </span>
      ) : null}
    </div>
  )
}
